import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

interface Suggestion {
  kind: "rep" | "deal" | "commission" | "invoice" | "page";
  title: string;
  subtitle?: string;
  href: string;
  score: number;
}

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, 120);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState(0);
  const navigate = useNavigate();

  // Global ⌘K / ctrl+K listener — also responds to "/" outside form fields.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (
        e.key === "/" &&
        !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Drive the native <dialog>: open / close + focus management.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
    if (!open && d.open) d.close();
    const onCancel = (e: Event) => {
      e.preventDefault();
      setOpen(false);
    };
    d.addEventListener("cancel", onCancel);
    return () => d.removeEventListener("cancel", onCancel);
  }, [open]);

  // Reset selection when results change.
  useEffect(() => setSelected(0), [debounced]);

  const { data } = useQuery({
    queryKey: ["search", debounced],
    queryFn: () => api.get<{ suggestions: Suggestion[] }>("/api/search", { searchParams: { q: debounced } }),
    enabled: open,
    staleTime: 10_000,
  });

  const suggestions = data?.suggestions ?? [];

  const go = useCallback(
    (s: Suggestion) => {
      setOpen(false);
      setQuery("");
      if (s.href.startsWith("http")) {
        window.open(s.href, "_blank", "noopener,noreferrer");
      } else {
        navigate({ to: s.href });
      }
    },
    [navigate],
  );

  function onInputKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((i) => Math.min(i + 1, Math.max(0, suggestions.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const s = suggestions[selected];
      if (s) go(s);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <dialog ref={dialogRef} className="cmdk-dialog" onClose={() => setOpen(false)}>
      <div className="cmdk-shell" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="cmdk-input"
          type="search"
          placeholder="Search reps, deals, invoices, pages…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKey}
          aria-label="Search"
        />
        <ul className="cmdk-list" role="listbox">
          {suggestions.length === 0 ? (
            <li className="cmdk-empty muted">{debounced ? "No matches." : "Start typing…"}</li>
          ) : (
            suggestions.map((s, idx) => (
              <li
                key={`${s.kind}:${s.href}:${idx}`}
                className={`cmdk-item${idx === selected ? " selected" : ""}`}
                role="option"
                aria-selected={idx === selected}
                onMouseEnter={() => setSelected(idx)}
                onClick={() => go(s)}
              >
                <span className={`cmdk-kind cmdk-kind--${s.kind}`}>{s.kind}</span>
                <span className="cmdk-title">{s.title}</span>
                {s.subtitle && <span className="cmdk-sub muted">{s.subtitle}</span>}
              </li>
            ))
          )}
        </ul>
        <div className="cmdk-footer">
          <span className="muted" style={{ fontSize: 11 }}>↑↓ navigate · ↵ open · esc close</span>
        </div>
      </div>
    </dialog>
  );
}
